import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { createAppSlice } from "../../app/createAppSlice";

import { getClientId } from "@common/helpers/funlib";
import { ng } from "@common/ngds";
import { selectProject } from "../projects/projectSlice";
type MaterialSectionState = {
  materials: any[];
  allMaterials: Record<string, any>;
  sections: any[];
  allSections: Record<string, any>;
  sectionFamilies: Record<string, string>;
  materialsStatus: "idle" | "loading" | "failed";
  sectionsStatus: "idle" | "loading" | "failed";
  materialRequestStatusByGuid: Record<string, "idle" | "loading" | "failed">;
  sectionRequestStatusByGuid: Record<string, "idle" | "loading" | "failed">;
};

export const fetchMaterials = createAsyncThunk(
  "materials/fetch",
  async (args: {}, thunkAPI) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };

    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "x-client-id": getClientId(),
    };

    const response = await fetch(`/api/materials/list`, {
      method: "GET",
      headers: tempheaders,
    });

    if (!response.ok) {
      throw new Error("Failed to fetch materials");
    }
    return await response.json();
  },
  {
    condition: (_args, { getState }) => {
      const state = getState() as { materialSection: { materialsStatus?: string } };
      return state.materialSection?.materialsStatus !== "loading";
    },
  },
);

export const changeMaterialConcrete = createAsyncThunk(
  "material/changeConcrete",
  async (
    args: {
      project: ng.frontend.beam.IProject;
    },
    thunkAPI,
  ) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "x-client-id": getClientId(),
      "Content-Type": "application/json",
    };

    const response = await fetch(`/api/projects/setDefaultRcData`, {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify(ng.frontend.column.Project.toObject(args.project)),
    });
    if (!response.ok) {
      throw new Error("Failed to change concrete material");
    }
    return await response.json();
  },
);

export const changeMaterialSteel = createAsyncThunk(
  "material/changeSteel",
  async (
    args: {
      project: ng.frontend.column.IProject;
    },
    thunkAPI,
  ) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "x-client-id": getClientId(),
      "Content-Type": "application/json",
    };

    const response = await fetch(`/api/projects/setDefaultSteelData`, {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify(ng.frontend.column.Project.toObject(args.project)),
    });
    if (!response.ok) {
      throw new Error("Failed to change steel material");
    }
    return await response.json();
  },
);

export const changeMaterialTimber = createAsyncThunk(
  "material/changeTimber",
  async (
    args: {
      project: ng.frontend.column.IProject;
    },
    thunkAPI,
  ) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "x-client-id": getClientId(),
      "Content-Type": "application/json",
    };

    const response = await fetch(`/api/projects/setDefaultTimberData`, {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify(ng.frontend.column.Project.toObject(args.project)),
    });
    if (!response.ok) {
      throw new Error("Failed to change timber material");
    }
    return await response.json();
  },
);

export const createCustomSection = createAsyncThunk(
  "section/create",
  async (args: { sectionData: ng.sections.Section_json }, thunkAPI) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "x-client-id": getClientId(),
      "Content-Type": "application/json",
    };

    const response = await fetch(`/api/sections/`, {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify(args.sectionData),
    });
    if (!response.ok) {
      throw new Error("Failed to create section");
    }
    return await response.json();
  },
);

export const editCustomSection = createAsyncThunk(
  "section/edit",
  async (args: { sectionData: ng.sections.Section_json }, thunkAPI) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "x-client-id": getClientId(),
      "Content-Type": "application/json",
    };

    const response = await fetch(`/api/sections/update`, {
      method: "POST",
      headers: tempheaders,
      body: JSON.stringify(args.sectionData),
    });
    if (!response.ok) {
      throw new Error("Failed to edit section");
    }
    return await response.json();
  },
);

export const deleteCustomSection = createAsyncThunk("section/delete", async (args: { guid: string }, thunkAPI) => {
  let state = thunkAPI.getState() as { UserSlice: { token: string | null } };
  const tempheaders = {
    authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
    "x-client-id": getClientId(),
    "Content-Type": "application/json",
  };

  const response = await fetch(`/api/sections/${args.guid}`, {
    method: "DELETE",
    headers: tempheaders,
  });
  if (!response.ok) {
    throw new Error("Failed to delete section");
  }
  return await response.json();
});

export const fetchSection = createAsyncThunk(
  "section/fetch",
  async (args: { guid: string }, thunkAPI) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };

    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "x-client-id": getClientId(),
    };

    const response = await fetch(`/api/sections/${args.guid}`, {
      method: "GET",
      headers: tempheaders,
    });

    if (!response.ok) {
      throw new Error("Failed to fetch section");
    }
    return await response.json();
  },
  {
    condition: (args, { getState }) => {
      const state = getState() as { materialSection: { sectionRequestStatusByGuid?: Record<string, string> } };
      return state.materialSection?.sectionRequestStatusByGuid?.[args.guid] !== "loading";
    },
  },
);

// A second request for a guid already being fetched shares the first one's result. It used to be
// cancelled instead (a `condition`), which resolved it with no payload: hovering a material in the
// reinforcement dropdown starts a fetch, so clicking it straight away silently failed to select it.
const materialFetchesInFlight = new Map<string, Promise<unknown>>();

export const fetchMaterial = createAsyncThunk("material/fetch", async (args: { guid: string }, thunkAPI) => {
  const inFlight = materialFetchesInFlight.get(args.guid);
  if (inFlight) {
    return await inFlight;
  }

  let state = thunkAPI.getState() as { UserSlice: { token: string | null } };

  const tempheaders = {
    authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
    "x-client-id": getClientId(),
  };

  const request = (async () => {
    const response = await fetch(`/api/materials/${args.guid}`, {
      method: "GET",
      headers: tempheaders,
    });

    if (!response.ok) {
      throw new Error("Failed to fetch materials");
    }
    return await response.json();
  })().finally(() => materialFetchesInFlight.delete(args.guid));

  materialFetchesInFlight.set(args.guid, request);
  return await request;
});

export const fetchSections = createAsyncThunk(
  "sections/fetch",
  async (args: {}, thunkAPI) => {
    let state = thunkAPI.getState() as { UserSlice: { token: string | null } };

    const tempheaders = {
      authorization: state.UserSlice.token ? `Bearer ${state.UserSlice.token}` : "",
      "x-client-id": getClientId(),
    };

    const response = await fetch(`/api/sections/list`, {
      method: "GET",
      headers: tempheaders,
    });

    if (!response.ok) {
      throw new Error("Failed to fetch sections");
    }
    return await response.json();
  },
  {
    condition: (_args, { getState }) => {
      const state = getState() as { materialSection: { sectionsStatus?: string } };
      return state.materialSection?.sectionsStatus !== "loading";
    },
  },
);

export function useMaterials() {
  const dispatch = useAppDispatch();
  let allSections = useAppSelector(selectAllSections);
  let project = useAppSelector(selectProject);
  const getMaterials = () => {
    dispatch(fetchMaterials({}));
  };
  const getSections = () => {
    dispatch(fetchSections({}));
  };

  const getSection = (guid: string) => {
    return dispatch(fetchSection({ guid }));
  };
  const createSection = async (sectionData: ng.sections.Section_json, type: string, group: string): Promise<any> => {
    let output = await dispatch(createCustomSection({ sectionData }));
    await getSections();
    return output;
  };

  const setConcreteMaterialForProject = async (project: ng.frontend.column.IProject): Promise<any> => {
    let output = (await dispatch(changeMaterialConcrete({ project }))).payload;

    return output;
  };

  const setSteelMaterialForProject = async (project: ng.frontend.column.IProject): Promise<any> => {
    let output = (await dispatch(changeMaterialSteel({ project }))).payload;
    return output;
  };
  const setTimberMaterialForProject = async (project: ng.frontend.column.IProject): Promise<any> => {
    let output = (await dispatch(changeMaterialTimber({ project }))).payload;
    return output;
  };

  const editSection = async (sectionData: ng.sections.Section_json): Promise<any> => {
    let output = await dispatch(editCustomSection({ sectionData }));
    await getSections();
    return output;
  };

  const deleteSection = async (guid: string): Promise<any> => {
    let output = await dispatch(deleteCustomSection({ guid: guid as string }));
    await getSections();
    return output;
  };

  const getMaterial = (guid: string) => {
    return dispatch(fetchMaterial({ guid }));
  };
  return {
    getMaterials,
    getSections,
    getSection,
    createSection,
    getMaterial,
    editSection,
    deleteSection,
    setConcreteMaterialForProject,
    setSteelMaterialForProject,
    setTimberMaterialForProject,
  };
}

export const materialSectionSlice = createAppSlice({
  name: "materialSection",
  initialState: {
    materials: [],
    allMaterials: {},
    sections: [],
    allSections: {},
    sectionFamilies: {},
    materialsStatus: "idle",
    sectionsStatus: "idle",
    materialRequestStatusByGuid: {},
    sectionRequestStatusByGuid: {},
  } as MaterialSectionState,

  reducers: {
    setMaterials(state, action) {
      state.materials = action.payload;
    },
    setSections(state, action) {
      state.sections = action.payload;
    },
    setSectionFamilies(state, action) {
      state.sectionFamilies = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMaterials.pending, (state) => {
      state.materialsStatus = "loading";
    });
    builder.addCase(fetchMaterials.fulfilled, (state, action) => {
      state.materialsStatus = "idle";
      state.allMaterials = action.payload.data ?? {};
      state.materials = action.payload.data?.Concrete ?? [];
    });
    builder.addCase(fetchMaterials.rejected, (state) => {
      state.materialsStatus = "failed";
    });
    builder.addCase(fetchMaterial.pending, (state, action) => {
      (state.materialRequestStatusByGuid as Record<string, "idle" | "loading" | "failed">)[action.meta.arg.guid] =
        "loading";
    });
    builder.addCase(fetchMaterial.fulfilled, (state, action) => {
      (state.materialRequestStatusByGuid as Record<string, "idle" | "loading" | "failed">)[action.meta.arg.guid] =
        "idle";
    });
    builder.addCase(fetchMaterial.rejected, (state, action) => {
      (state.materialRequestStatusByGuid as Record<string, "idle" | "loading" | "failed">)[action.meta.arg.guid] =
        "failed";
    });
    builder.addCase(fetchSection.pending, (state, action) => {
      (state.sectionRequestStatusByGuid as Record<string, "idle" | "loading" | "failed">)[action.meta.arg.guid] =
        "loading";
    });
    builder.addCase(fetchSection.fulfilled, (state, action) => {
      (state.sectionRequestStatusByGuid as Record<string, "idle" | "loading" | "failed">)[action.meta.arg.guid] =
        "idle";
    });
    builder.addCase(fetchSection.rejected, (state, action) => {
      (state.sectionRequestStatusByGuid as Record<string, "idle" | "loading" | "failed">)[action.meta.arg.guid] =
        "failed";
    });
    builder.addCase(fetchSections.pending, (state) => {
      state.sectionsStatus = "loading";
    });
    builder.addCase(changeMaterialConcrete.fulfilled, (state, action) => {});

    builder.addCase(fetchSections.fulfilled, (state, action) => {
      state.sectionsStatus = "idle";
      state.allSections = action.payload.data;
      state.sections = action.payload.data.Concrete;
      let secFamilies: any = {};
      const allCategoryData: any[] = Object.values(action.payload.data);
      for (const categoryData of allCategoryData) {
        if (typeof categoryData !== "object" || Array.isArray(categoryData)) continue;
        let secList: any[] = Object.values(categoryData).flat();
        for (const sec of secList) {
          if (sec.familyName) {
            secFamilies[sec.guid] = sec.familyName;
          }
        }
      }
      state.sectionFamilies = secFamilies;
    });
    builder.addCase(fetchSections.rejected, (state) => {
      state.sectionsStatus = "failed";
    });
  },
  selectors: {
    selectMaterials: (state: any) => state.materials,
    selectAllMaterials: (state: any) => state.allMaterials,
    selectSections: (state: any) => state.sections,
    selectAllSections: (state: any) => state.allSections,
    selectSectionFamilies: (state: any) => state.sectionFamilies,
    selectMaterialsStatus: (state: any) => state.materialsStatus,
    selectSectionsStatus: (state: any) => state.sectionsStatus,
    selectMaterialRequestStatusByGuid: (state: any) => state.materialRequestStatusByGuid,
    selectSectionRequestStatusByGuid: (state: any) => state.sectionRequestStatusByGuid,
  },
});

export const {
  selectMaterials,
  selectAllMaterials,
  selectSections,
  selectAllSections,
  selectSectionFamilies,
  selectMaterialsStatus,
  selectSectionsStatus,
  selectMaterialRequestStatusByGuid,
  selectSectionRequestStatusByGuid,
} = materialSectionSlice.selectors;
